import type { OpenCollaborationsSortColumn } from "@unbox/api";
import { BookOpen01, Plus, SearchMd } from "@untitledui/icons";
import { useState } from "react";
import { useOpenCollaborationsCount, useOpenCollaborationsList } from "@/api/collaborations";
import { usePagination } from "@/api/pagination";
import { useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { FilterPanelLegacy as FilterPanel } from "@/components/filters/filter-panel-legacy";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { useCache } from "@/hooks/use-cache";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useSort } from "@/hooks/use-sort";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import { CollabsListEmptyState } from "./collabs-list-empty-state";
import { OpenCollaborationsTable } from "./open-collaborations-table";

const OpenCollaborationsPageContent = () => {
  const [search, setSearch] = useState("");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<OpenCollaborationsSortColumn>("productTitle");

  const { reset } = useFilters();

  const { data: openData, isLoading: isLoadingOpen } = useOpenCollaborationsList({
    search: debouncedSearch,
    pagination: pagination.pagination,
    sort: sort ? { column: sort.column, direction: sort.direction } : undefined,
  });
  const { data: openCount } = useOpenCollaborationsCount(debouncedSearch);
  const cachedOpenCount = useCache(openCount);
  const loadedOpenTotal = useCache(openData?.total);
  const { data: shopStatus } = useTikTokShopConnectionStatus();

  const resetFilters = () => {
    setSearch("");
    clearSearch();
    reset();
    pagination.setPage(1);
  };

  // Genuine zero-data — or no Shop Access — replaces the whole page with the
  // full-bleed first-run empty state. A search keeps the inline table state.
  const shopUnauthorized = shopStatus ? !shopStatus.connected : false;
  if (!debouncedSearch && (shopUnauthorized || (!isLoadingOpen && (openData?.total ?? 0) === 0))) {
    return <CollabsListEmptyState variant="open" />;
  }

  if (!debouncedSearch && isLoadingOpen && loadedOpenTotal === undefined) return <LoadingPageState />;

  return (
    <PageLayout
      title="Open Collaborations"
      subtitle="Open to every TikTok Shop affiliate. You set the commission rate and creators opt in themselves."
      icon={BookOpen01}
      callToAction={
        <Tooltip title="Open Collaborations cannot currently be created in Unbox.">
          <TooltipTrigger>
            <span>
              <Button color="primary" size="sm" iconLeading={<Plus className="w-4 h-4" />} isDisabled>
                Create Collaboration
              </Button>
            </span>
          </TooltipTrigger>
        </Tooltip>
      }
    >
      <div className="flex flex-row items-center justify-end flex-shrink-0">
        <div className="flex items-center flex-row gap-2">
          <span className="text-sm font-medium text-tertiary mr-2">
            {isLoadingOpen ? "Loading..." : `${cachedOpenCount ?? 0} results`}
          </span>
          <Input
            size="sm"
            inputClassName="text-sm"
            className="w-64"
            value={search}
            onChange={setSearch}
            placeholder="Search collaboration name"
            icon={SearchMd}
          />
        </div>
      </div>

      <OpenCollaborationsTable
        rows={isLoadingOpen ? undefined : (openData?.collaborations ?? [])}
        sorting={{ descriptor, onSortChange: setFromDescriptor }}
        pagination={{
          page: pagination.page,
          totalPages: Math.ceil((openData?.total ?? 0) / pagination.pagination.limit),
          onPageChange: pagination.setPage,
        }}
        hasActiveFilters={Boolean(debouncedSearch)}
        onClearFilters={resetFilters}
      />

      <FilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} clearAll={() => resetFilters()} />
    </PageLayout>
  );
};

export const OpenCollaborationsPage = () => {
  return (
    <FiltersProvider activeFields={[]}>
      <OpenCollaborationsPageContent />
    </FiltersProvider>
  );
};
